import React, { useState, useEffect, useCallback } from 'react';
import { MdClose, MdSupport } from 'react-icons/md';
import { FaChartBar, FaChartPie, FaChartLine } from 'react-icons/fa';
import UserTypesChart from './UserTypesChart';
import TransactionChart from './TransactionChart';
import MmdaZoneGroupChart from './MmdaZoneGroupChart';

const ChartModal = ({ 
  isOpen, 
  onClose, 
  userRole, 
  userInfo, 
  dashboardData, 
  API_BASE_URL, 
  auth,
  onSupportChartOpen 
}) => {
  const [selectedChart, setSelectedChart] = useState('overview');
  const [loading, setLoading] = useState(false);

  const fetchChartData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch chart data based on user role and selected chart type
      console.log('Fetching chart data for:', userRole, selectedChart);

      if (userRole === 'Field Officer') {
        // Fetch field officer specific chart data
        if (userInfo?.officer_id) {
          const response = await fetch(
            `${API_BASE_URL}/officer-performance/officer/${userInfo.officer_id}`,
            auth
          );
          const performanceData = await response.json();
          console.log('Performance data:', performanceData);
        }
      } else if (userRole === 'MMDA Admin') {
        // Use existing dashboard data for MMDA
        console.log('MMDA dashboard data:', dashboardData);
      } else if (userRole === 'Super Admin') {
        // Use existing dashboard data for Super Admin
        console.log('Super Admin dashboard data:', dashboardData);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      setLoading(false);
    }
  }, [userRole, selectedChart, userInfo, API_BASE_URL, auth, dashboardData]);

  useEffect(() => {
    if (isOpen) {
      fetchChartData();
    }
  }, [isOpen, selectedChart]);

  const getAvailableCharts = () => {
    const charts = [];
    
    if (userRole === 'Field Officer') {
      charts.push(
        { id: 'performance', name: 'Performance Overview', icon: FaChartLine },
        { id: 'targets', name: 'Targets & Achievement', icon: FaChartBar }
      );
    } else if (userRole === 'MMDA Admin') {
      charts.push(
        { id: 'overview', name: 'MMDA Overview', icon: FaChartPie },
        { id: 'users', name: 'User Distribution', icon: FaChartBar },
        { id: 'transactions', name: 'Transaction Analysis', icon: FaChartLine },
        { id: 'zones', name: 'Zones & Groups', icon: FaChartBar }
      );
    } else if (userRole === 'Super Admin') {
      charts.push(
        { id: 'overview', name: 'System Overview', icon: FaChartPie },
        { id: 'users', name: 'User Distribution', icon: FaChartBar },
        { id: 'transactions', name: 'Transaction Analysis', icon: FaChartLine },
        { id: 'mmda', name: 'MMDA Distribution', icon: FaChartBar }
      );
    }
    
    return charts;
  };

  const canAccessSupportChart = () => {
    // Support button removed from Chart Analytics modal for all users
    return false;
  };

  const getUserRoleDisplayName = () => {
    switch (userRole) {
      case 'Field Officer':
        return 'Field Officer';
      case 'MMDA Admin':
        return 'MMDA Administrator';
      case 'Super Admin':
        return 'System Administrator';
      default:
        return userRole;
    }
  };

  const renderChart = () => {
    if (loading) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          color: '#666'
        }}>
          Loading chart data...
        </div>
      );
    }

    switch (selectedChart) {
      case 'users':
        return (
          <UserTypesChart 
            userTypesData={dashboardData?.userTypesData || []} 
            title="User Types Distribution"
          />
        );
      case 'transactions':
        return (
          <TransactionChart 
            transactionData={dashboardData?.transactionDistribution || []} 
            title="Transaction Distribution"
          />
        );
      case 'zones':
      case 'mmda':
        return (
          <MmdaZoneGroupChart 
            chartData={dashboardData?.mmdaZoneGroupData || dashboardData?.zoneGroupData || []} 
            title={selectedChart === 'zones' ? "Zones & Groups" : "MMDA Distribution"}
          />
        );
      case 'performance':
        // Field Officer performance chart
        return (
          <div style={{
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            margin: '20px'
          }}>
            <h3 style={{
              marginTop: 0,
              marginBottom: 15,
              color: '#1976d2',
              fontSize: 16,
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              Performance Overview
            </h3>
            <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>📊</div>
                <p>Performance analytics will be displayed here</p>
                <p style={{ fontSize: '14px', color: '#999' }}>
                  This will show your collection performance, target achievement, and trends
                </p>
              </div>
            </div>
          </div>
        );
      case 'targets':
        // Field Officer targets chart
        return (
          <div style={{
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            margin: '20px'
          }}>
            <h3 style={{
              marginTop: 0,
              marginBottom: 15,
              color: '#1976d2',
              fontSize: 16,
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              Targets & Achievement
            </h3>
            <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>🎯</div>
                <p>Target achievement analytics will be displayed here</p>
                <p style={{ fontSize: '14px', color: '#999' }}>
                  This will show your monthly targets, achievement rates, and progress tracking
                </p>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px',
            padding: '20px'
          }}>
            {(dashboardData?.userTypesData && dashboardData.userTypesData.length > 0) && (
              <UserTypesChart
                userTypesData={dashboardData.userTypesData}
                title="User Distribution"
              />
            )}
            {(dashboardData?.transactionDistribution && dashboardData.transactionDistribution.length > 0) && (
              <TransactionChart
                transactionData={dashboardData.transactionDistribution}
                title="Transactions"
              />
            )}
            {(!dashboardData?.userTypesData || dashboardData.userTypesData.length === 0) &&
             (!dashboardData?.transactionDistribution || dashboardData.transactionDistribution.length === 0) && (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#666',
                gridColumn: '1 / -1'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>📊</div>
                <h3>No Chart Data Available</h3>
                <p>Chart data will appear here once the dashboard loads.</p>
              </div>
            )}
          </div>
        );
    }
  };

  if (!isOpen) return null;

  const availableCharts = getAvailableCharts();

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        maxWidth: '90vw',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FaChartBar style={{ color: '#1976d2', fontSize: '20px' }} />
            <h3 style={{
              margin: 0,
              fontSize: 18,
              fontWeight: '600',
              color: '#333'
            }}>
              Chart Analytics - {getUserRoleDisplayName()}
            </h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {canAccessSupportChart() && (
              <button
                onClick={onSupportChartOpen}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#218838'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#28a745'}
              >
                <MdSupport size={16} />
                Support
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 8,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#666',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#e9ecef';
                e.currentTarget.style.color = '#333';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#666';
              }}
            >
              <MdClose size={20} />
            </button>
          </div>
        </div>

        {/* Chart Selection Tabs */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fff'
        }}>
          <div style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            {availableCharts.map(chart => (
              <button
                key={chart.id}
                onClick={() => setSelectedChart(chart.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  backgroundColor: selectedChart === chart.id ? '#1976d2' : '#f8f9fa',
                  color: selectedChart === chart.id ? 'white' : '#666',
                  border: '1px solid',
                  borderColor: selectedChart === chart.id ? '#1976d2' : '#e9ecef',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => {
                  if (selectedChart !== chart.id) {
                    e.currentTarget.style.backgroundColor = '#e3f2fd';
                    e.currentTarget.style.borderColor = '#1976d2';
                  }
                }}
                onMouseLeave={e => {
                  if (selectedChart !== chart.id) {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.borderColor = '#e9ecef';
                  }
                }}
              >
                <chart.icon size={14} />
                {chart.name}
              </button>
            ))}
          </div>
        </div>

        {/* Chart Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: '#f8f9fa'
        }}>
          {renderChart()}
        </div>
      </div>
    </div>
  );
};

export default ChartModal;
